import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Bell, BellOff, Loader2, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { UserNotification } from '../../types/userNotification';

function formatDate(value: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(date);
}

export function NotificationsButton() {
  const { userAccount } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dismissingAll, setDismissingAll] = useState(false);
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const lastUnreadRef = useRef<HTMLDivElement>(null);
  const pendingReadIdsRef = useRef(new Set<string>());
  const pendingReadOperationsRef = useRef(new Set<Promise<void>>());
  const dismissingAllRef = useRef(false);

  const loadNotifications = useCallback(async () => {
    if (!userAccount?.id) {
      setNotifications([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { getActiveExpertNotifications } = await import('../../services/notifications');
      setNotifications(await getActiveExpertNotifications(userAccount.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load notifications.');
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [userAccount?.id]);

  useEffect(() => {
    void loadNotifications();
    const interval = window.setInterval(() => void loadNotifications(), 5 * 60 * 1000);
    return () => window.clearInterval(interval);
  }, [loadNotifications]);

  const markNotificationsAsRead = useCallback((candidates: UserNotification[]): Promise<void> => {
    const unreadNotifications = candidates.filter((notification) => (
      notification.id
      && notification.status === 'unread'
      && !pendingReadIdsRef.current.has(notification.id)
    ));
    if (unreadNotifications.length === 0) return Promise.resolve();

    const unreadIds = new Set(unreadNotifications.map((notification) => notification.id));
    unreadIds.forEach((notificationId) => pendingReadIdsRef.current.add(notificationId));
    const readOn = new Date().toISOString();
    setNotifications((current) => current.map((notification) => (
      unreadIds.has(notification.id)
        ? { ...notification, status: 'read', readOn }
        : notification
    )));

    const operation = (async () => {
      try {
        const { setNotificationsToRead } = await import('../../services/notifications');
        await setNotificationsToRead(unreadNotifications);
      } catch {
        // Restore the server state if the read receipt could not be persisted.
        await loadNotifications();
      }
    })();

    pendingReadOperationsRef.current.add(operation);
    void operation.then(() => {
      pendingReadOperationsRef.current.delete(operation);
      unreadIds.forEach((notificationId) => pendingReadIdsRef.current.delete(notificationId));
    });
    return operation;
  }, [loadNotifications]);

  const closeMenu = useCallback(async () => {
    setOpen(false);
    if (dismissingAllRef.current) return;
    await markNotificationsAsRead(notifications);
  }, [markNotificationsAsRead, notifications]);

  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        void closeMenu();
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [closeMenu, open]);

  const unread = notifications.some((notification) => notification.status === 'unread');
  const lastUnreadIndex = notifications.reduce(
    (lastIndex, notification, index) => notification.status === 'unread' ? index : lastIndex,
    -1,
  );

  const dismissNotification = async (notification: UserNotification) => {
    if (!notification.id) return;
    setActionError(null);
    try {
      await Promise.all(Array.from(pendingReadOperationsRef.current));
      const { closeNotification } = await import('../../services/notifications');
      await closeNotification(notification.id);
      setNotifications((current) => current.filter((item) => item.id !== notification.id));
    } catch {
      setActionError('Could not dismiss the notification. Please try again.');
    }
  };

  const dismissAllNotifications = async () => {
    const notificationsToDismiss = notifications.filter((notification) => notification.id);
    if (notificationsToDismiss.length === 0) return;

    dismissingAllRef.current = true;
    setDismissingAll(true);
    setActionError(null);
    try {
      await Promise.all(Array.from(pendingReadOperationsRef.current));
      const { closeNotifications } = await import('../../services/notifications');
      await closeNotifications(notificationsToDismiss);
      const dismissedIds = new Set(notificationsToDismiss.map((notification) => notification.id));
      setNotifications((current) => current.filter((notification) => !dismissedIds.has(notification.id)));
    } catch {
      setActionError('Could not dismiss notifications. Please try again.');
      await loadNotifications();
    } finally {
      dismissingAllRef.current = false;
      setDismissingAll(false);
    }
  };

  const handleNotificationsScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const list = event.currentTarget;
    const lastUnread = lastUnreadRef.current;
    if (dismissingAllRef.current || !lastUnread || list.scrollTop <= 0) return;

    const listBottom = list.getBoundingClientRect().bottom;
    const lastUnreadBottom = lastUnread.getBoundingClientRect().bottom;
    if (lastUnreadBottom <= listBottom + 1) {
      void markNotificationsAsRead(notifications);
    }
  };

  const activateNotification = (notification: UserNotification) => {
    if (notification.redirectUrl) {
      setOpen(false);
      navigate(notification.redirectUrl);
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => {
          if (open) {
            void closeMenu();
          } else {
            setOpen(true);
          }
        }}
        aria-label="Notifications"
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-full text-white/85 hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/50"
      >
        <Bell className="h-5 w-5" />
        {unread ? <span data-testid="notification-unread-indicator" className="absolute right-2 top-2 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-primary" /> : null}
      </button>

      {open ? (
        <div className="absolute right-0 z-40 mt-2 w-[min(24rem,calc(100vw-2rem))] overflow-hidden rounded-md border border-slate-200 bg-white text-slate-900 shadow-lg">
          <div className="flex h-11 items-center gap-3 bg-primary px-4 text-white">
            <p className="mr-auto text-sm font-semibold">Notifications</p>
            {notifications.length > 0 && !loading ? (
              <button
                type="button"
                onClick={() => void dismissAllNotifications()}
                disabled={dismissingAll}
                className="text-xs font-medium underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-white/50 disabled:cursor-wait disabled:opacity-70"
              >
                {dismissingAll ? 'Dismissing…' : 'Dismiss all'}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => void closeMenu()}
              aria-label="Close notifications"
              className="rounded p-1 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/50"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {actionError ? (
            <p role="alert" className="border-b border-red-100 bg-red-50 px-4 py-2 text-xs text-red-700">
              {actionError}
            </p>
          ) : null}

          {loading ? (
            <div className="flex h-36 items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
            </div>
          ) : error ? (
            <p role="alert" className="px-4 py-6 text-sm text-red-700">
              {error}
            </p>
          ) : notifications.length === 0 ? (
            <div className="flex h-36 flex-col items-center justify-center gap-2 text-slate-500">
              <BellOff className="h-10 w-10" />
              <p className="text-sm">No notifications</p>
            </div>
          ) : (
            <div
              role="list"
              aria-label="Notification list"
              className="max-h-96 overflow-y-auto"
              onScroll={handleNotificationsScroll}
            >
              {notifications.map((notification, index) => (
                <div
                  key={notification.id}
                  ref={index === lastUnreadIndex ? lastUnreadRef : undefined}
                  role="listitem"
                  className="group flex gap-3 border-b border-slate-100 bg-slate-50 px-4 py-3 last:border-b-0"
                >
                  <button
                    type="button"
                    onClick={() => activateNotification(notification)}
                    className="min-w-0 flex-1 text-left"
                  >
                    <p className="text-sm leading-5 text-slate-800">{notification.message}</p>
                    {notification.createdOn ? <p className="mt-1 text-xs text-slate-500">{formatDate(notification.createdOn)}</p> : null}
                  </button>
                  <button
                    type="button"
                    onClick={() => void dismissNotification(notification)}
                    disabled={dismissingAll}
                    aria-label="Dismiss notification"
                    className="h-7 w-7 shrink-0 rounded p-1 text-slate-400 opacity-100 hover:bg-slate-200 hover:text-slate-700 disabled:cursor-wait disabled:opacity-50 sm:opacity-0 sm:group-hover:opacity-100"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
